import { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/styles';

import { GlobalLine, MainButton, Text, Location, UserInfoHeader } from '@bizzabo/design-system';

import { LocationType, ScheduledMeeting } from '../types';
import SchedulingOverlayWrapper from '../SchedulingOverlayWrapper';
import { useMeetings, View } from '../MeetingsTool/MeetingsToolContext';
import TimePicker from '../components/TimePicker';
import useSelectTimes from '../hooks/useSelectTimes';
import DeclineMeetingModal from './DeclineMeetingModal';
import MeetingScheduledModal from '../components/MeetingScheduledModal';
import LoaderButton from '../components/LoaderButton';
import useTypeTracking from '../../../analytics';
import InvitationLoader from './InvitationLoader';
import useOtherUser from '../hooks/useOtherUser';
import useScheduledMeeting from '../hooks/useScheduledMeeting';
import useSchedulingTimeZone from './useSchedulingTimezone';
import { FOLD_TIMES_AFTER } from '../constants';
import { useUserSettings } from '../../../settingsNav';
import ZeroAvailableTimesForInvitation from './ZeroAvailableTimesForInvitation';
import { TimeButtonType } from '../components/TimeButton/TimeButton';
import { parseScheduleMeeting } from '../hooks/parseScheduleMeeting';

const ONE_MINUTE = 1000 * 60;

const MeetingInvitation: React.FC = () => {
    const { eventId, setView, meetingInvitationArgs } = useMeetings();

    const { data: meeting, isLoading: isMeetingLoading } = useScheduledMeeting(
        eventId,
        meetingInvitationArgs.meetingId
    );
    const { data: otherUser, isLoading: isOtherUserLoading } = useOtherUser(meeting);
    const { data: timeZoneData, isLoading: isTimeZoneLoading } = useSchedulingTimeZone();
    const isLoading = isMeetingLoading || isOtherUserLoading || isTimeZoneLoading;

    const times = useMemo(
        () =>
            meeting?.times.map(time => ({
                time: new Date(time),
            })) || [],
        [meeting?.times]
    );
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
    const { openScheduledMeetings } = useUserSettings();
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [successMeeting, setSuccessMeeting] = useState<ScheduledMeeting>();
    const selectTimes = useSelectTimes();
    const { trackClickEvent, Track } = useTypeTracking({
        data: {
            description: {
                meeting_id: meeting?.id,
                locationType: meeting?.locationType,
                meeting_length: meeting?.duration / ONE_MINUTE,
                number_of_invitees: meeting?.participants.length - 1,
                invitation_text: meeting?.description,
                location: 'MEETING_CARD',
                flow: 'REVIEW_INVITATION',
                meeting_participant_role: 'GUEST',
                meeting_type: 'SCHEDULED',
            },
        },
    });

    const classes = useStyles();

    const onEditSettings = () => {
        openScheduledMeetings();
    };

    const onTimeSelect = (times: Date[]) => {
        setSelectedDates(times);
    };

    const onDeclineMeeting = () => {
        setIsDeclineModalOpen(true);
        trackClickEvent({ subCategory: 'MEETING_DECLINED_INITIATED' });
    };

    const closeMeetingScheduledModal = () => {
        setIsSuccessModalOpen(false);
        setView(View.Manage);
    };

    const onClickSchedule = () => {
        selectTimes.mutate(
            {
                eventId,
                meetingId: meeting.id,
                payload: {
                    selectedTimes: selectedDates,
                    timezone: timeZoneData.name,
                },
            },
            {
                onSuccess: res => {
                    const meeting = parseScheduleMeeting(res);
                    setSuccessMeeting(meeting);
                    setIsSuccessModalOpen(true);
                },
            }
        );
        trackClickEvent({ subCategory: 'MEETING_ACCEPTED' });
    };

    const handleClose = () => {
        setView(View.Manage);
        trackClickEvent({
            subCategory:
                meeting.times?.length === 0 ? 'MEETING_UNAVAILABLE_CLOSED' : 'CLOSE_INVITATION',
        });
    };

    let content = null;

    if (isLoading) {
        content = <InvitationLoader />;
    } else if (meeting.times?.length === 0) {
        content = (
            <ZeroAvailableTimesForInvitation
                eventId={eventId}
                meeting={meeting}
                otherAttendee={otherUser}
            />
        );
    } else {
        content = (
            <div className={classes.container}>
                <div className={classes.headerLine}>
                    <Text variant="t50">New Meeting Invitation</Text>
                </div>
                <div className={classes.userInfo}>
                    <UserInfoHeader user={otherUser} />
                </div>
                <div className={classes.content}>
                    <Text variant="t81" className={classes.introText}>
                        {meeting.intro}
                    </Text>
                    <div>
                        <div className={classes.titleIconContainer}>
                            <Location className={classes.infoIcon} />
                            <Text variant="t81" className={classes.infoLabel}>
                                Meeting Location:
                            </Text>
                        </div>
                        <Text variant="t81" className={classes.infoLineText}>
                            {meeting.locationType === LocationType.Virtual
                                ? 'Video Call (link will be provided)'
                                : meeting.physicalLocation}
                        </Text>
                    </div>
                    <div className={classes.timeZoneLine}>
                        <div className={classes.titleIconContainer}>
                            <GlobalLine className={classes.infoIcon} />
                            <Text variant="t81" className={classes.infoLabel}>
                                Time zone view:
                            </Text>
                        </div>
                        <Text variant="t81" className={classes.infoLineText}>
                            {timeZoneData.abbreviation}
                        </Text>
                        <a className={classes.link} onClick={onEditSettings}>
                            Edit settings
                        </a>
                    </div>

                    <div className={classes.proposedTimePicker}>
                        <TimePicker
                            variant={TimeButtonType.ToggleFill}
                            timeZone={timeZoneData.name}
                            times={times}
                            duration={meeting.duration}
                            selectedDates={selectedDates}
                            onChange={onTimeSelect}
                            foldAfter={FOLD_TIMES_AFTER}
                        />
                    </div>
                </div>

                <div className={classes.buttons}>
                    <LoaderButton
                        className={classes.scheduleButton}
                        isLoading={selectTimes.isLoading}
                        onClick={onClickSchedule}
                        disabled={selectedDates.length === 0}
                    >
                        <Text variant="t60">Schedule meeting</Text>
                    </LoaderButton>
                    <MainButton className={classes.declineButton} onClick={onDeclineMeeting}>
                        Decline meeting
                    </MainButton>
                </div>
            </div>
        );
    }

    return (
        <Track>
            <SchedulingOverlayWrapper onClose={handleClose}>
                {content}

                {isDeclineModalOpen && (
                    <DeclineMeetingModal
                        isOpen={isDeclineModalOpen}
                        meeting={meeting}
                        closeModal={() => setIsDeclineModalOpen(false)}
                    />
                )}

                {successMeeting && (
                    <MeetingScheduledModal
                        meeting={successMeeting}
                        isOpen={isSuccessModalOpen}
                        closeModal={closeMeetingScheduledModal}
                    />
                )}
            </SchedulingOverlayWrapper>
        </Track>
    );
};

const useStyles = makeStyles(
    theme => ({
        container: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            margin: '0 -32px 16px',
            color: theme.customization.background.G10,
        },
        headerLine: {
            marginBottom: 22,
            padding: '0 32px',
        },
        userInfo: {
            marginBottom: 16,
            padding: '0 32px',
        },
        content: {
            flex: 1,
            padding: '0 32px',
            overflowY: 'auto',
        },
        proposedTimePicker: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
        },
        buttons: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '16px 32px 0',
        },
        scheduleButton: {
            width: '100%',
            minHeight: 48,
            padding: 7,
            marginBottom: 8,
        },
        declineButton: {
            width: '100%',
            border: 0,
            '&:hover': {
                border: 0,
                background: 'transparent',
            },
            '& > div': {
                paddingTop: 2,
                paddingBottom: 2,
            },
        },
        infoLineText: {
            display: 'inline',
            verticalAlign: 'middle',
        },
        titleIconContainer: {
            display: 'inline-flex',
            verticalAlign: 'middle',
        },
        infoIcon: {
            minWidth: 13,
            margin: '1px 4px 0 0',
            fontSize: 15,
            fontWeight: 600,
            color: theme.customization.background.G10,
        },
        infoLabel: {
            whiteSpace: 'nowrap',
            overflow: 'initial',
            fontWeight: 600,
            marginRight: 4,
        },
        introText: {
            paddingBottom: 15,
        },
        timeZoneLine: {
            marginTop: 8,
            marginBottom: 32,
        },
        link: {
            ...theme.typography.t81,
            cursor: 'pointer',
            textDecoration: 'underline',
            marginLeft: 4,
            verticalAlign: 'middle',
        },
    }),
    { classNamePrefix: 'meetings-meeting-invitation' }
);

export default MeetingInvitation;
